import {
  Button,
  Card,
  DatePicker,
  Flex,
  Input,
  InputRef,
  message,
  Modal,
  Select,
  Tag,
  theme,
  Tooltip,
} from 'antd';
import { useCityList } from 'apis/home';
import LOCALS from 'commons/locals';
import { findLabelByValue } from 'commons/options';
import { Trans } from 'react-i18next';
import { useAppSelector } from 'store/hooks';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { getLocalStorageLanguage } from 'commons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { editUmsMemberTags, updateUmsMemberV2 } from 'apis/ums';
import { SelectOption } from 'types/base';
import { useToggle } from 'react-use';
import useSysDict from 'commons/hooks/use-sys-dict';
import { Edit, Info, Save, X } from 'lucide-react';
import { MemberBaseInfoProps } from './types';
import dayjs from 'dayjs';
import i18n from 'i18n';
import { LANGUAGE_MAP } from 'constant/language';
import { SOCIAL_MEDIA_OPTION_LIST } from 'constant/social-media';
import { MEMBER_CREATE_SOURCE_OPTION_LIST } from 'constant/member-create-source';
import { GENDER_OPTION_LIST } from 'constant/ums-member';

type MemberEditData = Parameters<typeof updateUmsMemberV2>[1] & {
  firstName: string;
  lastName: string;
  phone: string;
  gender: number;
  createSource: number;
  country: string;
};

const languageOptions = [
  { value: LANGUAGE_MAP.EN, label: 'English' },
  { value: LANGUAGE_MAP.JA, label: 'Japanese' },
  { value: LANGUAGE_MAP.ZH_CN, label: '中文' },
  { value: LANGUAGE_MAP.ZH_TW, label: '繁体中文' },
];

const tagInputStyle: React.CSSProperties = {
  width: 64,
  height: 22,
  marginInlineEnd: 8,
  verticalAlign: 'top',
};

const MemberBaseInfo = ({
  member,
  onSaveSuccess,
}: MemberBaseInfoProps & {
  onSaveSuccess: () => Promise<void> | undefined;
}) => {
  const {
    countryCode,
    phone,
    firstName,
    lastName,
    firstNameKatakana,
    lastNameKatakana,
    gender,
    birthday,
    createSource,
    country,
    city,
    detailAddress,
    socialAccount,
    socialName,
    remark,
    idCertificate,
    paymentDetails,
    language,
  } = member;
  const [isEditing, toggleIsEditing] = useToggle(false);
  const [isSaving, setIsSaving] = useState(false);

  const [tags, setTags] = useState<string[]>(member.tags || []);
  const [inputValue, setInputValue] = useState('');
  const [editInputIndex, setEditInputIndex] = useState(-1);
  const [editInputValue, setEditInputValue] = useState('');
  const editInputRef = useRef<InputRef>(null);
  const { countryOptions, countryCodeOptions } =
    useAppSelector(selectGlobalInfo);
  const { cityList } = useCityList(country);
  const [isModalOpen, toggleIsModalOpen] = useToggle(false);
  const tagConfig = useSysDict<[string[], { [key: string]: string }]>(
    'ums_member_tags_config',
  );

  useEffect(() => {
    editInputRef.current?.focus();
  }, [editInputValue]);

  const handleClose = useCallback(
    (removedTag: string) => {
      const newTags = tags.filter((tag) => tag !== removedTag);
      editUmsMemberTags({ id: member.id, tags: newTags });
      setTags(newTags);
    },
    [member.id, tags],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    },
    [],
  );

  const handleInputConfirm = useCallback(
    async (inputValue: string) => {
      if (inputValue && !tags.includes(inputValue)) {
        setTags([...tags, inputValue]);
        editUmsMemberTags({ id: member.id, tags: [...tags, inputValue] });
      }
      setInputValue('');
    },
    [member.id, tags],
  );

  const handleEditInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEditInputValue(e.target.value);
    },
    [],
  );

  const handleEditInputConfirm = useCallback(() => {
    const newTags = [...tags];
    newTags[editInputIndex] = editInputValue;
    setTags(newTags);
    editUmsMemberTags({ id: member.id, tags: newTags });
    setEditInputIndex(-1);
    setEditInputValue('');
  }, [editInputIndex, editInputValue, member.id, tags]);

  const { token } = theme.useToken();

  const tagPlusStyle: React.CSSProperties = useMemo(() => {
    return {
      height: 22,
      background: token.colorBgContainer,
      borderStyle: 'dashed',
    };
  }, [token.colorBgContainer]);

  const [userEditData, setUserEditData] = useState<MemberEditData>({
    firstName,
    lastName,
    firstNameKatakana,
    lastNameKatakana,
    countryCode,
    phone,
    gender,
    birthday,
    createSource,
    socialName,
    socialAccount,
    country,
    city,
    detailAddress,
    idCertificate,
    paymentDetails,
    remark,
    language,
  });

  const handleSave = useCallback(() => {
    setIsSaving(true);
    updateUmsMemberV2(member.id, userEditData).then(() => {
      setIsSaving(false);
      onSaveSuccess()?.then(() => {
        toggleIsEditing(false);
        message.success(i18n.t(LOCALS.successful_operation));
      });
    });
  }, [member.id, onSaveSuccess, toggleIsEditing, userEditData]);

  const { cityList: editCityList, loading: editCityListLoading } = useCityList(
    userEditData.country,
  );

  const baseInfoList = useMemo(() => {
    const isEn = getLocalStorageLanguage() === LANGUAGE_MAP.EN;
    let list: {
      label: string | React.ReactNode;
      node: React.ReactNode;
      key: string;
    }[] = [];

    if (isEn) {
      list = [
        ...list,
        {
          key: 'first_name',
          label: <Trans i18nKey={LOCALS.first_name} />,
          node: isEditing ? (
            <Input
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              value={userEditData.firstName}
              onChange={(e) =>
                setUserEditData({ ...userEditData, firstName: e.target.value })
              }
            />
          ) : (
            firstName || '-'
          ),
        },
        {
          key: 'last_name',
          label: <Trans i18nKey={LOCALS.last_name} />,
          node: isEditing ? (
            <Input
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              value={userEditData.lastName}
              onChange={(e) =>
                setUserEditData({ ...userEditData, lastName: e.target.value })
              }
            />
          ) : (
            lastName || '-'
          ),
        },
        {
          key: 'first_name_katakana',
          label: <Trans i18nKey={LOCALS.first_name_katakana} />,
          node: isEditing ? (
            <Input
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              value={userEditData.firstNameKatakana}
              onChange={(e) =>
                setUserEditData({
                  ...userEditData,
                  firstNameKatakana: e.target.value,
                })
              }
            />
          ) : (
            firstNameKatakana || '-'
          ),
        },
        {
          key: 'last_name_katakana',
          label: <Trans i18nKey={LOCALS.last_name_katakana} />,
          node: isEditing ? (
            <Input
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              value={userEditData.lastNameKatakana}
              onChange={(e) =>
                setUserEditData({
                  ...userEditData,
                  lastNameKatakana: e.target.value,
                })
              }
            />
          ) : (
            lastNameKatakana || '-'
          ),
        },
      ];
    } else {
      list = [
        ...list,
        {
          key: 'last_name',
          label: <Trans i18nKey={LOCALS.last_name} />,
          node: isEditing ? (
            <Input
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              value={userEditData.lastName}
              onChange={(e) =>
                setUserEditData({ ...userEditData, lastName: e.target.value })
              }
            />
          ) : (
            lastName || '-'
          ),
        },
        {
          key: 'first_name',
          label: <Trans i18nKey={LOCALS.first_name} />,
          node: isEditing ? (
            <Input
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              value={userEditData.firstName}
              onChange={(e) =>
                setUserEditData({ ...userEditData, firstName: e.target.value })
              }
            />
          ) : (
            firstName || '-'
          ),
        },
        {
          key: 'last_name_katakana',
          label: <Trans i18nKey={LOCALS.last_name_katakana} />,
          node: isEditing ? (
            <Input
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              value={userEditData.lastNameKatakana}
              onChange={(e) =>
                setUserEditData({
                  ...userEditData,
                  lastNameKatakana: e.target.value,
                })
              }
            />
          ) : (
            lastNameKatakana || '-'
          ),
        },
        {
          key: 'first_name_katakana',
          label: <Trans i18nKey={LOCALS.first_name_katakana} />,
          node: isEditing ? (
            <Input
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              value={userEditData.firstNameKatakana}
              onChange={(e) =>
                setUserEditData({
                  ...userEditData,
                  firstNameKatakana: e.target.value,
                })
              }
            />
          ) : (
            firstNameKatakana || '-'
          ),
        },
      ];
    }

    list = [
      ...list,
      ...[
        {
          key: 'phone',
          label: <Trans i18nKey={LOCALS.phone_number} />,
          node: isEditing ? (
            <div className="flex items-center gap-2 md:flex-row flex-col">
              <Select
                className="w-full"
                placeholder={<Trans i18nKey={LOCALS.please_select} />}
                style={{
                  minWidth: 168,
                }}
                showSearch
                filterOption={(
                  input: string,
                  option: SelectOption | undefined,
                ) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={countryCodeOptions}
                value={userEditData.countryCode}
                onChange={(countryCode) =>
                  setUserEditData({ ...userEditData, countryCode })
                }
              />
              <Input
                placeholder={i18n.t(LOCALS.please_enter) || ''}
                value={userEditData.phone}
                onChange={(e) =>
                  setUserEditData({ ...userEditData, phone: e.target.value })
                }
              />
            </div>
          ) : (
            <div>
              {countryCode && <span>+{countryCode} </span>}{' '}
              <span>{phone || '-'}</span>
            </div>
          ),
        },
        {
          key: 'gender',
          label: <Trans i18nKey={LOCALS.gender} />,
          node: isEditing ? (
            <Select
              className="w-full"
              options={GENDER_OPTION_LIST}
              value={userEditData.gender}
              onChange={(gender) =>
                setUserEditData({ ...userEditData, gender })
              }
            />
          ) : (
            findLabelByValue(gender, GENDER_OPTION_LIST)
          ),
        },
        {
          key: 'birthday',
          label: <Trans i18nKey={LOCALS.birthday} />,
          node: isEditing ? (
            <DatePicker
              placeholder={i18n.t(LOCALS.please_select) || ''}
              className="w-full"
              value={
                userEditData.birthday ? dayjs(userEditData.birthday) : null
              }
              onChange={(date) =>
                setUserEditData({
                  ...userEditData,
                  birthday: date ? date.format('YYYY-MM-DD') : null,
                })
              }
            />
          ) : (
            birthday || '-'
          ),
        },
        {
          key: 'created_from',
          label: <Trans i18nKey={LOCALS.created_from} />,
          node: isEditing ? (
            <Select
              placeholder={<Trans i18nKey={LOCALS.please_select} />}
              className="w-full"
              options={MEMBER_CREATE_SOURCE_OPTION_LIST}
              value={userEditData.createSource}
              onChange={(createSource) =>
                setUserEditData({ ...userEditData, createSource })
              }
            />
          ) : (
            findLabelByValue(createSource, MEMBER_CREATE_SOURCE_OPTION_LIST)
          ),
        },
        {
          key: 'social_media',
          label: <Trans i18nKey={LOCALS.social_media} />,
          node: isEditing ? (
            <Select
              className="w-full"
              placeholder={i18n.t(LOCALS.please_select) || ''}
              options={SOCIAL_MEDIA_OPTION_LIST}
              value={userEditData.socialName}
              onChange={(socialName) =>
                setUserEditData({ ...userEditData, socialName })
              }
            />
          ) : (
            findLabelByValue(socialName, SOCIAL_MEDIA_OPTION_LIST)
          ),
        },
        {
          key: 'social_handle',
          label: <Trans i18nKey={LOCALS.social_handle} />,
          node: isEditing ? (
            <Input
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              value={userEditData.socialAccount}
              onChange={(e) =>
                setUserEditData({
                  ...userEditData,
                  socialAccount: e.target.value,
                })
              }
            />
          ) : (
            socialAccount || '-'
          ),
        },
        {
          key: 'language',
          label: <Trans i18nKey={LOCALS.language} />,
          node: isEditing ? (
            <Select
              className="w-full"
              options={languageOptions}
              value={userEditData.language}
              onChange={(language) =>
                setUserEditData({ ...userEditData, language })
              }
            />
          ) : (
            findLabelByValue(language, languageOptions)
          ),
        },
        {
          key: 'country_region',
          label: <Trans i18nKey={LOCALS.country_region} />,
          node: isEditing ? (
            <Select
              className="w-full"
              showSearch
              filterOption={(input: string, option: SelectOption | undefined) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={countryOptions}
              value={userEditData.country}
              onChange={(country) =>
                setUserEditData({ ...userEditData, country, city: undefined })
              }
              placeholder={<Trans i18nKey={LOCALS.please_select} />}
            />
          ) : (
            findLabelByValue(country, countryOptions)
          ),
        },
        {
          key: 'state_province_city',
          label: <Trans i18nKey={LOCALS.state_province_city} />,
          node: isEditing ? (
            <Select
              disabled={editCityListLoading}
              placeholder={i18n.t(LOCALS.please_select) || ''}
              showSearch
              filterOption={(input: string, option: SelectOption | undefined) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              className="w-full"
              options={editCityList
                .map(({ name, id }) => {
                  return {
                    value: id.toString(),
                    label: name,
                  };
                })
                .sort((a, b) => a.label.localeCompare(b.label))}
              value={userEditData.city}
              onChange={(city) => setUserEditData({ ...userEditData, city })}
            />
          ) : city ? (
            <div className="break-words">
              {editCityList.find(({ id }) => String(id) === String(city))
                ?.name ||
                cityList.find(({ id }) => String(id) === String(city))?.name ||
                city}
            </div>
          ) : (
            '-'
          ),
        },
        {
          key: 'detail_address',
          label: <Trans i18nKey={LOCALS.detail_address} />,
          node: isEditing ? (
            <Input.TextArea
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              autoSize={{ minRows: 2, maxRows: 6 }}
              value={userEditData.detailAddress}
              onChange={(e) =>
                setUserEditData({
                  ...userEditData,
                  detailAddress: e.target.value,
                })
              }
            />
          ) : (
            <div className="whitespace-pre-wrap break-words">
              {detailAddress || '-'}
            </div>
          ),
        },
        {
          key: 'id_certificate',
          label: <Trans i18nKey={LOCALS.id_certificate} />,
          node: isEditing ? (
            <Input.TextArea
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              autoSize={{ minRows: 2, maxRows: 6 }}
              value={userEditData.idCertificate}
              onChange={(e) =>
                setUserEditData({
                  ...userEditData,
                  idCertificate: e.target.value,
                })
              }
            />
          ) : (
            <div className="whitespace-pre-wrap break-words">
              {idCertificate || '-'}
            </div>
          ),
        },
        {
          key: 'payment_details',
          label: <Trans i18nKey={LOCALS.payment_details} />,
          node: isEditing ? (
            <Input.TextArea
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              autoSize={{ minRows: 2, maxRows: 6 }}
              value={userEditData.paymentDetails}
              onChange={(e) =>
                setUserEditData({
                  ...userEditData,
                  paymentDetails: e.target.value,
                })
              }
            />
          ) : (
            <div className="whitespace-pre-wrap break-words">
              {paymentDetails || '-'}
            </div>
          ),
        },
        {
          key: 'note',
          label: <Trans i18nKey={LOCALS.note} />,
          node: isEditing ? (
            <Input.TextArea
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              autoSize={{ minRows: 2, maxRows: 6 }}
              value={userEditData.remark}
              onChange={(e) =>
                setUserEditData({ ...userEditData, remark: e.target.value })
              }
            />
          ) : (
            <div className="whitespace-pre-wrap break-words">
              {remark || '-'}
            </div>
          ),
        },
        {
          key: 'khvsAbbLJs',
          label: <Trans i18nKey={LOCALS.khvsAbbLJs} />,
          node: (
            <>
              <Modal
                title="New Tag"
                open={isModalOpen}
                footer={null}
                onCancel={toggleIsModalOpen}
              >
                <div className="grid gap-4">
                  <div className="flex gap-1">
                    {tagConfig &&
                      tagConfig[0].map((tag) => {
                        return (
                          <Tag
                            color={tagConfig[1][tag] || ''}
                            className="cursor-pointer"
                            key={tag}
                            onClick={() => {
                              handleInputConfirm(tag);
                            }}
                          >
                            {tag}
                          </Tag>
                        );
                      })}
                  </div>
                  <Input
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={() => {
                      handleInputConfirm(inputValue);
                    }}
                    onPressEnter={() => {
                      handleInputConfirm(inputValue);
                    }}
                  ></Input>
                </div>
              </Modal>
              <Flex gap="4px 0" wrap="wrap">
                {tags.map<React.ReactNode>((tag, index) => {
                  if (editInputIndex === index) {
                    return (
                      <Input
                        ref={editInputRef}
                        key={tag}
                        size="small"
                        style={tagInputStyle}
                        value={editInputValue}
                        onChange={handleEditInputChange}
                        onBlur={handleEditInputConfirm}
                        onPressEnter={handleEditInputConfirm}
                      />
                    );
                  }

                  const isLongTag = tag.length > 20;
                  const tagElem = (
                    <Tag
                      key={tag}
                      closable={true}
                      style={{ userSelect: 'none' }}
                      onClose={() => handleClose(tag)}
                      color={(tagConfig && tagConfig[1][tag]) || ''}
                    >
                      <span
                        onDoubleClick={(e) => {
                          setEditInputIndex(index);
                          setEditInputValue(tag);
                          e.preventDefault();
                        }}
                      >
                        {isLongTag ? `${tag.slice(0, 20)}...` : tag}
                      </span>
                    </Tag>
                  );
                  return isLongTag ? (
                    <Tooltip title={tag} key={tag}>
                      {tagElem}
                    </Tooltip>
                  ) : (
                    tagElem
                  );
                })}

                <Tag
                  className="cursor-pointer"
                  style={tagPlusStyle}
                  icon={<PlusOutlined />}
                  onClick={toggleIsModalOpen}
                >
                  New Tag
                </Tag>
              </Flex>
            </>
          ),
        },
      ],
    ];

    return list;
  }, [
    isEditing,
    countryCodeOptions,
    userEditData,
    countryCode,
    phone,
    gender,
    birthday,
    createSource,
    socialName,
    socialAccount,
    language,
    countryOptions,
    country,
    editCityListLoading,
    editCityList,
    city,
    cityList,
    detailAddress,
    idCertificate,
    paymentDetails,
    remark,
    isModalOpen,
    toggleIsModalOpen,
    tagConfig,
    inputValue,
    handleInputChange,
    tags,
    tagPlusStyle,
    firstName,
    lastName,
    firstNameKatakana,
    lastNameKatakana,
    handleInputConfirm,
    editInputIndex,
    editInputValue,
    handleEditInputChange,
    handleEditInputConfirm,
    handleClose,
  ]);

  return (
    <Card bodyStyle={{ padding: '16px' }}>
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2 text-base font-medium">
          <Info className="w-5 h-5" />
          <Trans i18nKey={LOCALS.basic_info} /> 1
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <Button
              className="flex items-center gap-2"
              onClick={() => toggleIsEditing(false)}
            >
              <X className="w-4 h-4" />
              <Trans i18nKey={LOCALS.cancel} />
            </Button>
          ) : (
            <Button
              className="flex items-center gap-2"
              onClick={() => toggleIsEditing(true)}
            >
              <Edit className="w-4 h-4" />
              <Trans i18nKey={LOCALS.edit} />
            </Button>
          )}
          <Button
            type="primary"
            loading={isSaving}
            disabled={!isEditing || isSaving}
            className="flex items-center gap-2"
            onClick={handleSave}
          >
            <Save className="w-4 h-4" />
            <Trans i18nKey={LOCALS.save} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-5">
        {baseInfoList.map((item) => {
          return (
            <div key={item.key}>
              <div className="text-sm font-medium text-gray-500">
                {item.label}
              </div>
              <div className="mt-1 text-sm font-medium">{item.node}</div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default MemberBaseInfo;
